import { Button } from '../../../atoms/Button/Button';
import { Icon } from '../../../atoms/Icon/Icon';
import type { CardChrome } from '../../widgets/shared/cardShell';
import grid from '../../widgets/shared/moduleGrid.module.css';

/**
 * The drag handle and the remove button for a widget that is a `Table` rather
 * than a `Card` (prototype `views/cards/tableChrome.tsx`).
 *
 * `Card` gets these from `CardHeader`'s `Type=Draggable` (996:30988), which a
 * table has no equivalent of — its `Table Header` is a different component. But
 * that header has an `actions` slot, and the two controls are the same pair of
 * Ghost Grey icon-only buttons, so they go there and a table widget drags and
 * closes like every other one.
 *
 * Not in Figma: no frame puts a table on a report. Flagged in plan.md §7.
 */
export type TableWidgetActionsProps = { chrome?: CardChrome; title: string };

export function TableWidgetActions({ chrome, title }: TableWidgetActionsProps) {
  if (!chrome) return null;
  const { dragHandle, onRemove, removeLabel = 'Remove' } = chrome;
  if (!dragHandle && !onRemove) return null;

  return (
    <>
      {dragHandle && (
        <Button
          hierarchy="ghostGrey"
          iconLeading={<Icon name="draggable" />}
          aria-label={dragHandle.label ?? `Reorder ${title}`}
          aria-describedby={dragHandle.describedBy}
          aria-pressed={dragHandle.pressed}
          onPointerDown={dragHandle.onPointerDown}
          onKeyDown={dragHandle.onKeyDown}
          ref={dragHandle.ref}
        />
      )}
      {onRemove && (
        <Button
          hierarchy="ghostGrey"
          iconLeading={<Icon name="close" />}
          onClick={onRemove}
          aria-label={`${removeLabel} ${title}`}
        />
      )}
    </>
  );
}

/**
 * `Card`'s `.dragging` for a table — `border/focus` and `shadow/lg` while it is
 * picked up. A `Table` draws its own box, so it needs its own rule.
 */
export const tableDraggingClass = (chrome?: CardChrome) =>
  chrome?.dragging ? grid.draggingTable : undefined;
